import React, { useState, useEffect } from "react";
import { fetchTenantList, updateTenant, Tenant, setTenantLoading } from "../../features/outlet/TenantSlice";
import { X } from "lucide-react";
import { useAppDispatch } from "../../store/hook";
import CustomModal from "../common/CustomModal";

/* --------------------------------- Interfaces ------------------------------ */

interface FileUploadInterface {
  file?: File | string;
  link?: string;
}

interface EditTenantProps {
  editingTenant: Tenant | null;
  setEditingTenant: (tenant: Tenant | null) => void;
  principalId: string | number | null;
  programId: string | number | null;
  setShowEditModal: (showEditModal: boolean) => void;
  showEditModal: boolean;
}

interface FormErrors {
  tenantName: string;
}

const EditTenant: React.FC<EditTenantProps> = ({ editingTenant, principalId, programId, setEditingTenant, setShowEditModal, showEditModal }) => {
  /* --------------------------------- Redux ------------------------------ */
  const dispatch = useAppDispatch();

  /* --------------------------------- States ------------------------------ */
  const [newStoreImage, setNewStoreImage] = useState<FileUploadInterface>({});
  // const [newTermCondition, setNewTermCondition] = useState<FileUploadInterface>({});
  const [newStoreLogo, setNewStoreLogo] = useState<FileUploadInterface>({});
  const [formErrors, setFormErrors] = useState<FormErrors>({
    tenantName: ''
  });

  /* --------------------------------- Hooks ------------------------------ */
  useEffect(() => {
    // if (editingTenant?.termNCondition) {
    //   setNewTermCondition((prevState) => {
    //     return {
    //       ...prevState,
    //       link: editingTenant.termNCondition ? editingTenant.termNCondition : "",
    //     };
    //   });
    // }
    if (editingTenant?.storeImage) {
      setNewStoreImage((prevState) => {
        return {
          ...prevState,
          link: editingTenant.storeImage ? editingTenant.storeImage : "",
        };
      });
    }
    if (editingTenant?.storeLogo) {
      setNewStoreLogo((prevState) => {
        return {
          ...prevState,
          link: editingTenant.storeLogo ? editingTenant.storeLogo : "",
        };
      });
    }
  }, [editingTenant]);

  /* ------------------------------ Event Handlers ---------------------------- */

  const validateForm = (): boolean => {
    let isValid = true;
    const errors: FormErrors = {
      tenantName: ''
    };

    if (!editingTenant?.tenantName?.trim()) {
      errors.tenantName = 'Tenant name is required';
      isValid = false;
    }

    // Email validation only if email is provided
    if (editingTenant?.email?.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editingTenant.email)) {
        // Add email to errors interface temporarily for this validation
        (errors as any).email = 'Please enter a valid email address';
        isValid = false;
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleEditSubmit = async () => {
    if (editingTenant && validateForm()) {
      const formData = new FormData();
      formData.append("tenantName", editingTenant.tenantName);

      if(editingTenant.email) formData.append("email", editingTenant.email);
      // Add the new optional fields
      if (editingTenant.tenantBrand) formData.append("tenantBrand", editingTenant.tenantBrand);
      if (editingTenant.wing) formData.append("wing", editingTenant.wing);
      if (editingTenant.floor) formData.append("floor", editingTenant.floor);
      if (editingTenant.address) formData.append("address", editingTenant.address);
      if (editingTenant.city) formData.append("city", editingTenant.city);
      if (editingTenant.state) formData.append("state", editingTenant.state);
      if (editingTenant.storeCoordinator) formData.append("storeCoordinator", editingTenant.storeCoordinator);
      
      // Handle file uploads
      if ("file" in newStoreImage) formData.append("storeImage", newStoreImage.file ? newStoreImage.file : "");
      if ("file" in newStoreLogo) formData.append("storeLogo", newStoreLogo.file ? newStoreLogo.file : "");
      // if ("file" in newTermCondition) formData.append("termCondition", newTermCondition.file ? newTermCondition.file : "");

      await dispatch(updateTenant({ tenantId: editingTenant.tenantId, formData }));
      setShowEditModal(false);
      setEditingTenant(null);
      setNewStoreImage({});
      // setNewTermCondition({});
      setNewStoreLogo({});

      // Refresh tenant list
      if (principalId && programId) {
        dispatch(setTenantLoading());
        setTimeout(() => {
          dispatch(fetchTenantList({ principalId: principalId, programId: programId }));
        }, 1000);
      } else if (principalId) {
        dispatch(setTenantLoading());
        setTimeout(() => {
          dispatch(fetchTenantList({ principalId: principalId }));
        }, 1000);
      } else {
        dispatch(setTenantLoading());
        setTimeout(() => {
          dispatch(fetchTenantList({}));
        }, 1000);
      }
    }
  };

  const readFileAsUrl = (file: File | string): string => {
    if (typeof file === "string") {
      return "";
    }
    return URL.createObjectURL(file);
  };

  const resetModal = () => {
    setShowEditModal(false);
    setEditingTenant(null);
    setFormErrors({
      tenantName: ''
    });
  };

  /* ------------------------------ UI Handlers ---------------------------- */

  if (!showEditModal) return null;

  // Helper for image preview UI
  const renderImagePreview = (fileObj: FileUploadInterface, onRemove: () => void, alt: string) => (
    <div className="form-preview relative flex items-center justify-center border border-gray-200 rounded-md h-16 w-full bg-gray-50 overflow-hidden">
      {fileObj.file || fileObj.link ? (
        <>
          <button
            type="button"
            className="absolute top-1 right-1 text-gray-400 hover:text-red-500 z-10 bg-white rounded-full p-0.5 shadow"
            onClick={onRemove}
            aria-label="Remove image"
          >
            <X size={18} />
          </button>
          <img
            src={typeof fileObj.file === 'string' && fileObj.file ? fileObj.file : fileObj.link ? fileObj.link : fileObj.file ? readFileAsUrl(fileObj.file) : ''}
            alt={alt}
            className="h-full object-contain"
          />
        </>
      ) : null}
    </div>
  );

  return (
    <CustomModal
      show={showEditModal}
      onHide={resetModal}
      title="Edit Tenant"
      width="w-[600px]"
    >
      {editingTenant && (
        <form
          onSubmit={e => {
            e.preventDefault();
            handleEditSubmit();
          }}
          className="flex flex-col max-h-[70vh] w-full"
        >
          <div className="flex-1 overflow-y-auto pr-2">
            <div className="space-y-5">
          {/* Tenant Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tenant Name</label>
            <input
              type="text"
              value={editingTenant.tenantName}
              onChange={e => setEditingTenant({ ...editingTenant, tenantName: e.target.value })}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none transition-colors ${formErrors.tenantName ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="Enter tenant name"
            />
            {formErrors.tenantName && (
              <p className="mt-1 text-sm text-red-600">{formErrors.tenantName}</p>
            )}
          </div>
          {/* Tenant Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tenant Email</label>
            <input
              type="email"
              value={editingTenant.email || ''}
              onChange={e => setEditingTenant({ ...editingTenant, email: e.target.value })}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none transition-colors ${(formErrors as any).email ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="Enter tenant email"
            />
            {(formErrors as any).email && (
              <p className="mt-1 text-sm text-red-600">{(formErrors as any).email}</p>
            )}
          </div>

          {/* Tenant Brand */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tenant Brand</label>
            <input
              type="text"
              value={editingTenant.tenantBrand || ''}
              onChange={e => setEditingTenant({ ...editingTenant, tenantBrand: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none transition-colors"
              placeholder="Enter tenant brand"
            />
          </div>

          {/* Wing */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Wing</label>
            <input
              type="text"
              value={editingTenant.wing || ''}
              onChange={e => setEditingTenant({ ...editingTenant, wing: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none transition-colors"
              placeholder="Enter wing"
            />
          </div>

          {/* Floor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Floor</label>
            <input
              type="text"
              value={editingTenant.floor || ''}
              onChange={e => setEditingTenant({ ...editingTenant, floor: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none transition-colors"
              placeholder="Enter floor"
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <textarea
              value={editingTenant.address || ''}
              onChange={e => setEditingTenant({ ...editingTenant, address: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none transition-colors"
              placeholder="Enter address"
              rows={2}
            />
          </div>

          {/* City and State in a row */}
          <div className="grid grid-cols-2 gap-4">
            {/* City */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                value={editingTenant.city || ''}
                onChange={e => setEditingTenant({ ...editingTenant, city: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none transition-colors"
                placeholder="Enter city"
              />
            </div>

            {/* State */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <input
                type="text"
                value={editingTenant.state || ''}
                onChange={e => setEditingTenant({ ...editingTenant, state: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none transition-colors"
                placeholder="Enter state"
              />
            </div>
          </div>

          {/* Store Coordinator */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Store Coordinator</label>
            <input
              type="text"
              value={editingTenant.storeCoordinator || ''}
              onChange={e => setEditingTenant({ ...editingTenant, storeCoordinator: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none transition-colors"
              placeholder="Enter store coordinator name"
            />
          </div>

          {/* Store Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Store Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={e => setNewStoreImage({ file: e.target.files?.[0] })}
              className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 mb-2"
            />
            {renderImagePreview(newStoreImage, () => setNewStoreImage({ link: '', file: '' }), 'Store')}
          </div>
          {/* Store Logo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Store Logo</label>
            <input
              type="file"
              accept="image/*"
              onChange={e => setNewStoreLogo({ file: e.target.files?.[0] })}
              className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 mb-2"
            />
            {renderImagePreview(newStoreLogo, () => setNewStoreLogo({ link: '', file: '' }), 'Logo')}
            </div>
          </div>
          </div>
          
          {/* Actions - Fixed at bottom */}
          <div className="flex justify-between pt-4 border-t border-gray-200 mt-4">
            <button
              type="button"
              className="px-4 py-2 rounded-md text-sm font-medium text-white bg-brand hover:bg-brand/80 focus:outline-none focus:border-0 focus:ring-1 focus:ring-brand transition-colors"
              onClick={resetModal}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-md text-sm font-medium text-white bg-brand hover:bg-brand/80 focus:outline-none focus:border-0 focus:ring-1 focus:ring-brand transition-colors"
            >
              Save Changes
            </button>
          </div>
        </form>
      )}
    </CustomModal>
  );
};

export default EditTenant;
